import React, {useState, useEffect, useMemo} from 'react';
import { Menu, X, Server, ChevronDown, MapPin, Terminal, Camera } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { smoothScrollToWithOffset } from '../lib/scroll';
import { openBilling } from '../lib/referral';
import { visibleLocations } from '../data/mock';
import { useDisabledLocationCodes } from '../data/liveLocations';


// Wi-Fi inside a circle — same VPN icon used in the BILLpanel client
// sidebar so the marketing site and the billing UI share visual identity.
function SvgVpn({ size = 20, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M7 11 a7 7 0 0 1 10 0" />
      <path d="M9 13.5 a4 4 0 0 1 6 0" />
      <circle cx="12" cy="16" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [isLocationsOpen, setIsLocationsOpen] = useState(false);
  const [isMobileLocationsOpen, setIsMobileLocationsOpen] = useState(false);
  const [locationsCloseTimeout, setLocationsCloseTimeout] = useState(null);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);
  const [productsCloseTimeout, setProductsCloseTimeout] = useState(null);
  const { language, toggleLanguage, t } = useLanguage();
  const disabledCodes = useDisabledLocationCodes(); // выключенные датацентры из админки
  const navigate = useNavigate();
  const location = useLocation();
  const [logoTs, setLogoTs] = useState(0);
  useEffect(() => { setLogoTs(Date.now()); }, [location.pathname]);

  const languages = [
    { code: 'en', name: 'English', flag: '/images/locations/flag-gb.png' },
    { code: 'ru', name: 'Русский', flag: '/images/locations/flag-ru.png' }
  ];

  const currentLanguage = languages.find(lang => lang.code === language);

  // Helper для создания URL с учетом языка
  const getLocalizedPath = (path) => {
    if (language === 'ru') {
      return '/ru' + path;
    }
    return path;
  };

  // "Products" is rendered as a custom dropdown (VPS + VPN), not a regular
  // navigationItems entry. The remaining items go through the normal map
  // below.
  const navigationItems = [
    { name: t('header.pricing'), href: '#pricing' },
    { name: t('header.locations'), href: '#locations' },
    { name: t('header.support'), href: getLocalizedPath('/support') }
  ];

  const handleNavClick = (href) => {
    if (href.startsWith('#')) {
      const elementId = href.substring(1);
      // Если такой якорь УЖЕ есть на текущей странице (например, продуктовый
      // лендинг со своим прайс-блоком) — скроллим локально, без navigate.
      if (typeof document !== 'undefined' && document.getElementById(elementId)) {
        smoothScrollToWithOffset(elementId, -200);
        return;
      }
      // Иначе — переходим на главную и скроллим там.
      const homePath = getLocalizedPath('/');
      const currentPath = location.pathname.replace(/^\/ru/, '') || '/';
      if (currentPath !== '/') {
        navigate(homePath);
        setTimeout(() => {
          smoothScrollToWithOffset(elementId, -200);
        }, 100);
      } else {
        smoothScrollToWithOffset(elementId, -200);
      }
    } else {
      // Обычная навигация для не-якорных ссылок
      navigate(getLocalizedPath(href));
    }
  };

  const scrollToPricing = () => {
    if (typeof document !== 'undefined' && document.getElementById('pricing')) {
      smoothScrollToWithOffset('pricing', -200);
      return;
    }
    const homePath = getLocalizedPath('/');
    const currentPath = location.pathname.replace(/^\/ru/, '') || '/';
    if (currentPath !== '/') {
      navigate(homePath);
      setTimeout(() => {
        smoothScrollToWithOffset('pricing', -200);
      }, 100);
    } else {
      smoothScrollToWithOffset('pricing', -200);
    }
  };

  return (
    <header className="sticky top-0 bg-white/80 backdrop-blur-xl border-b border-slate-200/60 z-[1000]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20 relative py-3 md:py-5">
          {/* Logo */}
          <Link to={getLocalizedPath('/')} className="flex items-center cursor-pointer hover:opacity-80 transition-opacity">
            <img 
              key={location.pathname + '-header-logo-' + logoTs}
              src={logoTs ? `/logo-horizontal-light-animated.svg?t=${logoTs}` : `/logo-horizontal-light-animated.svg`}
              alt="NetGrid Host" 
              className="h-11 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-7">
            {/* Products dropdown — VPS + VPN, replaces the old "Home" link */}
            <div
              className="relative group"
              onMouseEnter={() => {
                if (productsCloseTimeout) {
                  clearTimeout(productsCloseTimeout);
                  setProductsCloseTimeout(null);
                }
                setIsProductsOpen(true);
              }}
              onMouseLeave={() => {
                const timeout = setTimeout(() => setIsProductsOpen(false), 200);
                setProductsCloseTimeout(timeout);
              }}
            >
              <button className="text-slate-600 hover:text-blue-700 font-semibold transition-colors relative group cursor-pointer flex items-center gap-1">
                {language === 'ru' ? 'Продукты' : 'Products'}
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isProductsOpen ? 'rotate-180' : ''}`} />
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-700 group-hover:w-full transition-all duration-300"></span>
              </button>
              {isProductsOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white border border-gray-200 rounded-xl overflow-hidden z-[1003]">
                  <Link
                    to={getLocalizedPath('/')}
                    onClick={() => setIsProductsOpen(false)}
                    className="flex items-start gap-3 px-4 py-3.5 hover:bg-blue-50 transition-colors cursor-pointer group/item"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-50 group-hover/item:bg-blue-100 flex items-center justify-center flex-shrink-0 transition-colors">
                      <Server className="w-5 h-5 text-blue-700" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-slate-900">{language === 'ru' ? 'VPS — Виртуальные серверы' : 'VPS — Virtual Servers'}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{language === 'ru' ? 'KVM VPS от €2.98/мес — 13 локаций' : 'KVM VPS from €2.98/mo — 13 locations'}</div>
                    </div>
                  </Link>
                  <Link
                    to={getLocalizedPath('/vpn')}
                    onClick={() => setIsProductsOpen(false)}
                    className="flex items-start gap-3 px-4 py-3.5 hover:bg-blue-50 transition-colors cursor-pointer group/item border-t border-gray-100"
                  >
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 group-hover/item:bg-indigo-100 flex items-center justify-center flex-shrink-0 transition-colors">
                      <SvgVpn className="w-5 h-5 text-indigo-700" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-slate-900">{language === 'ru' ? 'VPN' : 'VPN'}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{language === 'ru' ? 'Личный VPN с выделенным IP — от €6/мес' : 'Personal VPN with dedicated IP — from €6/mo'}</div>
                    </div>
                  </Link>
                  <Link
                    to={getLocalizedPath('/snapshots')}
                    onClick={() => setIsProductsOpen(false)}
                    className="flex items-start gap-3 px-4 py-3.5 hover:bg-blue-50 transition-colors cursor-pointer group/item border-t border-gray-100"
                  >
                    <div className="w-10 h-10 rounded-lg bg-sky-50 group-hover/item:bg-sky-100 flex items-center justify-center flex-shrink-0 transition-colors">
                      <Camera className="w-5 h-5 text-sky-700" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-slate-900">{language === 'ru' ? 'Снапшоты' : 'Snapshots'}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{language === 'ru' ? 'Точка восстановления VPS в один клик' : 'One-click VPS restore points'}</div>
                    </div>
                  </Link>
                  <Link
                    to={getLocalizedPath('/developers')}
                    onClick={() => setIsProductsOpen(false)}
                    className="flex items-start gap-3 px-4 py-3.5 hover:bg-blue-50 transition-colors cursor-pointer group/item border-t border-gray-100"
                  >
                    <div className="w-10 h-10 rounded-lg bg-emerald-50 group-hover/item:bg-emerald-100 flex items-center justify-center flex-shrink-0 transition-colors">
                      <Terminal className="w-5 h-5 text-emerald-700" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-slate-900">API</div>
                      <div className="text-xs text-slate-500 mt-0.5">{language === 'ru' ? 'REST API — управление из кода' : 'REST API — manage from code'}</div>
                    </div>
                  </Link>

                </div>
              )}
            </div>
            {navigationItems.map((item) => {
              // Special handling for Locations menu
              if (item.name === t('header.locations')) {
                return (
                  <div 
                    key={item.name}
                    className="relative group"
                    onMouseEnter={() => {
                      if (locationsCloseTimeout) {
                        clearTimeout(locationsCloseTimeout);
                        setLocationsCloseTimeout(null);
                      }
                      setIsLocationsOpen(true);
                    }}
                    onMouseLeave={() => {
                      const timeout = setTimeout(() => {
                        setIsLocationsOpen(false);
                      }, 200);
                      setLocationsCloseTimeout(timeout);
                    }}
                  >
                    <button
                      className="text-slate-600 hover:text-blue-700 font-semibold transition-colors relative group cursor-pointer flex items-center gap-1"
                    >
                      {item.name}
                      <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isLocationsOpen ? 'rotate-180' : ''}`} />
                      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-700 group-hover:w-full transition-all duration-300"></span>
                    </button>
                    
                    {/* Modern Dropdown — soft, breathing */}
                    {isLocationsOpen && (
                      <div 
                        className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-white border border-slate-200 rounded-2xl z-[1003] w-[640px] overflow-hidden"
                        onMouseEnter={() => {
                          if (locationsCloseTimeout) {
                            clearTimeout(locationsCloseTimeout);
                            setLocationsCloseTimeout(null);
                          }
                        }}
                        onMouseLeave={() => {
                          const timeout = setTimeout(() => {
                            setIsLocationsOpen(false);
                          }, 200);
                          setLocationsCloseTimeout(timeout);
                        }}
                      >
                        <div className="px-5 pt-4 pb-2">
                          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                            {language === 'en' ? 'Our datacenters' : 'Наши дата-центры'}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-1 px-3 pb-3">
                          {visibleLocations(language, disabledCodes).map((loc) => {
                            const countrySlug = loc.country.toLowerCase().replace(/\s+/g, '-');
                            const routes = {
                              'netherlands': '/vps-netherlands',
                              'нидерланды': '/vps-netherlands',
                              'germany': '/vps-germany',
                              'германия': '/vps-germany',
                              'poland': '/vps-poland',
                              'польша': '/vps-poland',
                              'latvia': '/vps-latvia',
                              'латвия': '/vps-latvia',
                              'czech-republic': '/vps-czechia',
                              'чехия': '/vps-czechia',
                              'italy': '/vps-italy',
                              'италия': '/vps-italy',
                              'switzerland': '/vps-switzerland',
                              'швейцария': '/vps-switzerland',
                              'usa': '/vps-miami',
                              'сша': '/vps-miami',
                              'bulgaria': '/vps-bulgaria',
                              'болгария': '/vps-bulgaria',
                              'greece': '/vps-greece',
                              'греция': '/vps-greece',
                              'spain': '/vps-spain',
                              'ukraine': '/vps-ukraine',
                              'украина': '/vps-ukraine',
                              'испания': '/vps-spain',
                              'romania': '/vps-romania',
                              'румыния': '/vps-romania'
                            };
                            const route = routes[countrySlug] || '/';
                            return (
                              <Link
                                key={loc.id}
                                to={getLocalizedPath(route)}
                                onClick={() => setIsLocationsOpen(false)}
                                className="group flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-all"
                              >
                                <img 
                                  src={loc.flag} 
                                  alt={loc.country + ' flag'}
                                  loading="lazy"
                                  className="w-9 h-6 object-cover rounded-md ring-1 ring-slate-200/60 flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-slate-900 truncate">
                                    {loc.country}
                                  </div>
                                  <div className="text-xs text-slate-500 truncate">
                                    {loc.city}
                                  </div>
                                </div>
                                <ChevronDown className="w-3.5 h-3.5 text-slate-300 -rotate-90 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-blue-700 transition-all" />
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              
              // Regular navigation items
              return (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={(e) => {
                    if (item.href.startsWith('#')) {
                      e.preventDefault();
                      handleNavClick(item.href);
                    }
                  }}
                  className="text-slate-600 hover:text-blue-700 font-semibold transition-colors relative group cursor-pointer"
                >
                  {item.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-700 group-hover:w-full transition-all duration-300"></span>
                </a>
              );
            })}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4 relative z-[1001]">
            {/* Language Switcher */}
            <div className="relative z-[1002]">
              <button
                onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-slate-50 hover:bg-slate-100 text-slate-700 transition-colors border border-slate-200"
              >
                
                <span className="text-xs font-medium">{currentLanguage.code.toUpperCase()}</span>
                <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${isLangDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isLangDropdownOpen && (
                <div className="absolute right-0 mt-1.5 w-36 bg-white border border-gray-200 rounded-md overflow-hidden z-[1003]">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        toggleLanguage();
                        setIsLangDropdownOpen(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-blue-50 transition-colors duration-200 text-sm ${
                        language === lang.code ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                      }`}
                    >
                      
                      <span className="font-medium">{lang.code.toUpperCase()}</span>
                      {language === lang.code && (
                        <span className="ml-auto text-xs text-blue-700">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {location.pathname === '/affiliate' ? (
              <>
                <Button 
                  variant="ghost" 
                  className="text-slate-600 hover:text-blue-700 hover:bg-blue-50 transition-colors font-semibold"
                  onClick={() => openBilling('logon', language)}
                >
                  {t('header.login')}
                </Button>
                <Button 
                  onClick={() => openBilling('register', language)}
                  className="bg-blue-700 hover:bg-blue-800 text-white rounded-full transition-all font-medium"
                >
                  {t('affiliate.hero.joinNow')}
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  className="text-slate-600 hover:text-blue-700 hover:bg-blue-50 transition-colors font-semibold"
                  onClick={() => openBilling('logon', language)}
                >
                  {t('header.login')}
                </Button>
                <Button 
                  onClick={() => openBilling('register', language)}
                  className="bg-blue-700 hover:bg-blue-800 text-white transition-all font-semibold rounded-full"
                >
                  {t('hero.signUp')}
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-gray-900 hover:bg-white/50 transition-colors duration-300"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4 bg-white backdrop-blur-md">
            <div className="flex flex-col space-y-2">
              {/* Products dropdown — VPS + VPN */}
              <div>
                <button
                  onClick={() => setIsMobileProductsOpen(!isMobileProductsOpen)}
                  className="w-full text-left text-slate-700 hover:text-blue-700 font-semibold py-2 transition-colors duration-300 flex items-center justify-between"
                >
                  {language === 'ru' ? 'Продукты' : 'Products'}
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isMobileProductsOpen ? 'rotate-180' : ''}`} />
                </button>
                {isMobileProductsOpen && (
                  <div className="mt-2 ml-4 space-y-2 border-l-2 border-blue-200 pl-4">
                    <Link
                      to={getLocalizedPath('/')}
                      onClick={() => { setIsMenuOpen(false); setIsMobileProductsOpen(false); }}
                      className="flex items-start gap-3 py-2 text-slate-700 hover:text-blue-700 transition-colors"
                    >
                      <Server className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm font-bold">{language === 'ru' ? 'VPS — Виртуальные серверы' : 'VPS — Virtual Servers'}</div>
                        <div className="text-xs text-slate-500">{language === 'ru' ? 'KVM VPS от €2.98/мес' : 'KVM VPS from €2.98/mo'}</div>
                      </div>
                    </Link>
                    <Link
                      to={getLocalizedPath('/vpn')}
                      onClick={() => { setIsMenuOpen(false); setIsMobileProductsOpen(false); }}
                      className="flex items-start gap-3 py-2 text-slate-700 hover:text-blue-700 transition-colors"
                    >
                      <SvgVpn className="w-5 h-5 text-indigo-700 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm font-bold">{language === 'ru' ? 'VPN' : 'VPN'}</div>
                        <div className="text-xs text-slate-500">{language === 'ru' ? 'Личный VPN от €6/мес' : 'Personal VPN from €6/mo'}</div>
                      </div>
                    </Link>
                    <Link
                      to={getLocalizedPath('/snapshots')}
                      onClick={() => { setIsMenuOpen(false); setIsMobileProductsOpen(false); }}
                      className="flex items-start gap-3 py-2 text-slate-700 hover:text-blue-700 transition-colors"
                    >
                      <Camera className="w-5 h-5 text-sky-700 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm font-bold">{language === 'ru' ? 'Снапшоты' : 'Snapshots'}</div>
                        <div className="text-xs text-slate-500">{language === 'ru' ? 'Точка восстановления VPS' : 'VPS restore points'}</div>
                      </div>
                    </Link>
                    <Link
                      to={getLocalizedPath('/developers')}
                      onClick={() => { setIsMenuOpen(false); setIsMobileProductsOpen(false); }}
                      className="flex items-start gap-3 py-2 text-slate-700 hover:text-blue-700 transition-colors"
                    >
                      <Terminal className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm font-bold">API</div>
                        <div className="text-xs text-slate-500">{language === 'ru' ? 'REST API из кода' : 'REST API from code'}</div>
                      </div>
                    </Link>

                  </div>
                )}
              </div>
              {navigationItems.map((item) => {
                // Special handling for Locations in mobile
                if (item.name === t('header.locations')) {
                  return (
                    <div key={item.name}>
                      <button
                        onClick={() => setIsMobileLocationsOpen(!isMobileLocationsOpen)}
                        className="w-full text-left text-slate-700 hover:text-blue-700 font-medium py-2 transition-colors duration-300 flex items-center justify-between"
                      >
                        {item.name}
                        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isMobileLocationsOpen ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {isMobileLocationsOpen && (
                        <div className="mt-2 ml-4 space-y-2 max-h-64 overflow-y-auto border-l-2 border-blue-200 pl-4">
                          {visibleLocations(language, disabledCodes).map((loc) => (
                            <button
                              key={loc.id}
                              onClick={() => {
                                const countrySlug = loc.country.toLowerCase().replace(/\s+/g, '-');
                                const routes = {
                                  'netherlands': '/vps-netherlands',
                                  'нидерланды': '/vps-netherlands',
                                  'germany': '/vps-germany',
                                  'германия': '/vps-germany',
                                  'poland': '/vps-poland',
                                  'польша': '/vps-poland',
                                  'latvia': '/vps-latvia',
                                  'латвия': '/vps-latvia',
                                  'czech-republic': '/vps-czechia',
                                  'чехия': '/vps-czechia',
                                  'italy': '/vps-italy',
                                  'италия': '/vps-italy',
                                  'switzerland': '/vps-switzerland',
                                  'швейцария': '/vps-switzerland',
                                  'usa': '/vps-miami',
                                  'сша': '/vps-miami',
                                  'bulgaria': '/vps-bulgaria',
                                  'болгария': '/vps-bulgaria',
                                  'greece': '/vps-greece',
                                  'греция': '/vps-greece',
                                  'spain': '/vps-spain',
                                  'испания': '/vps-spain',
                                  'romania': '/vps-romania',
                                  'румыния': '/vps-romania'
                                };
                                const route = routes[countrySlug] || '/';
                                navigate(getLocalizedPath(route));
                                setIsMenuOpen(false);
                                setIsMobileLocationsOpen(false);
                              }}
                              className="w-full flex items-center gap-3 py-2 hover:text-blue-700 transition-colors duration-300"
                            >
                              <img 
                                src={loc.flag} 
                                alt={`${loc.country} VPS hosting location flag`}
                                loading="lazy"
                                className="w-6 h-6 object-cover rounded"
                              />
                              <div className="text-left">
                                <div className="text-sm font-semibold">VPS {loc.country}</div>
                                <div className="text-xs text-gray-500">{loc.city}</div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                
                // Regular mobile navigation items
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => {
                      if (item.href.startsWith('#')) {
                        e.preventDefault();
                        handleNavClick(item.href);
                      }
                      setIsMenuOpen(false);
                    }}
                    className="text-slate-700 hover:text-blue-700 font-semibold py-2 transition-colors duration-300"
                  >
                    {item.name}
                  </a>
                );
              })}
              <div className="flex flex-col space-y-2 pt-4 border-t border-gray-200">
                <Button 
                  variant="ghost" 
                  className="justify-start text-slate-600 hover:text-blue-700 hover:bg-blue-50 transition-colors font-semibold"
                  onClick={() => openBilling('logon', language)}
                >
                  {t('header.login')}
                </Button>
                {location.pathname === '/affiliate' ? (
                  <Button 
                    onClick={() => {
                      openBilling('register', language);
                      setIsMenuOpen(false);
                    }}
                    className="bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-full"
                  >
                    {t('affiliate.hero.joinNow')}
                  </Button>
                ) : (
                  <Button 
                    onClick={() => {
                      openBilling('register', language);
                      setIsMenuOpen(false);
                    }}
                    className="bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-full"
                  >
                    {t('hero.signUp')}
                  </Button>
                )}

                {/* Mobile Language Switcher */}
                <div className="pt-2 border-t border-gray-200 mt-2">
                  <div className="text-xs text-gray-500 mb-2 px-2">{t('header.language')}</div>
                  <div className="flex gap-2">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => toggleLanguage()}
                        className={`flex-1 flex items-center justify-center gap-2 h-9 px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                          language === lang.code 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                        }`}
                      >
                        
                        <span>{lang.code.toUpperCase()}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};